// Canonical mapping for Talmia product enums (mirrors the C# ProductStatus /
// ProductType enums). Numeric values are stored on TMProduct.Status and
// TMProduct.ProductType; these helpers turn them into human-friendly labels.

// ProductStatus: Active = 1, Archived = 88, Deleted = 99
export const PRODUCT_STATUS = {
  1: "Active",
  88: "Archived",
  99: "Deleted",
};

// ProductType: None = 0 then sequential. Labels are human-friendly.
export const PRODUCT_TYPE = {
  0: "None",
  1: "Ablative",
  2: "Access Key",
  3: "Access Panel",
  4: "Acoustic",
  5: "Adhesive",
  6: "Aircrete",
  7: "Baffle",
  8: "Base Profile",
  9: "Board",
  10: "Bonding Compound",
  11: "Bracket",
  12: "Breather Membrane",
  13: "Brick",
  14: "Building Sand",
  15: "Building Slab",
  16: "Cavity Closer",
  17: "Cavity Stop",
  18: "Cavity Wall",
  19: "Ceiling",
  20: "Ceiling Pad",
  21: "Ceiling Tile",
  22: "Cement",
  23: "Chipboard",
  24: "Clip",
  25: "Closer",
  26: "Collated Clipped Head Nail",
  27: "Combination Ladder",
  28: "Concrete",
  29: "Construction Board",
  30: "DPC",
  31: "Earthwool Acoustic",
  32: "EasiFill",
  33: "Edging Strip",
  34: "Extension Ladder",
  35: "Fence",
  36: "Fibre Board",
  37: "Filler",
  38: "Fire Stop",
  39: "Fire Stop",
  40: "Fixing",
  41: "Flooring",
  42: "Foam",
  43: "Frost Protection",
  44: "Garden",
  45: "Glue",
  46: "Gravel Board",
  47: "Hardboard Sheet",
  48: "HardieBacker Board",
  49: "Hopper",
  50: "Hush Panel",
  51: "Insulated DPC",
  52: "Insulation",
  53: "Insulation Board",
  54: "Insulation Disc",
  55: "Insulation Retaining Clip",
  56: "Insulation Slab",
  57: "Intumescent",
  58: "Joint Filler",
  59: "Ladder",
  60: "Lime",
  61: "Loft Door",
  62: "MDF Board",
  63: "Membrane",
  64: "Mesh",
  65: "Mesh Sheet",
  66: "Metal",
  67: "Mortar",
  68: "Multi Acoustic Roll",
  69: "Multifoil Insulation",
  70: "Multi Layer Foil Insulation",
  71: "Paint",
  72: "Panel",
  73: "Paving Slab",
  74: "Pea Shingle",
  75: "Pipe Plain Ended",
  76: "PIR Insulation Board",
  77: "Plaster",
  78: "Plasterboard",
  79: "Platform Step",
  80: "Plywood",
  81: "Plywood Door",
  82: "Plywood Sheet",
  83: "Polystyrene Insulation",
  84: "Post Mix",
  85: "Rainscreen",
  86: "RCM Cemboard",
  87: "RCM Y Wall",
  88: "Render",
  89: "Render Bead",
  90: "Roof",
  91: "Screw",
  92: "Scrim Tape",
  93: "Sealant",
  94: "Sharp Sand",
  95: "Silicone",
  96: "Skirting Board",
  97: "Slab",
  98: "Socket",
  99: "Solvent",
  100: "Staple",
  101: "Stepladder",
  102: "Stop Bead",
  103: "Structural OSB",
  104: "Structural Plywood",
  105: "Tape",
  106: "Tape Measure",
  107: "Tarpaulin",
  108: "Timber",
  109: "Type Cavity",
  110: "Underfloor",
  111: "Wall Plug",
  112: "Wirehanger",
  113: "Brick And Blocks",
  114: "Cement And Limes",
  115: "CLS Timber",
  116: "Decking",
  117: "Fencing",
  118: "Landscaping",
  119: "Sand And Aggregates",
  120: "Sheet Materials",
  121: "Treated Timber",
  122: "Accessories for Insulation",
  123: "Building Interior Ceiling",
  124: "Acoustic Foam",
  125: "Acoustic Wall Tiles",
  126: "Noise Barrier Fence",
  127: "Loft Roll Insulation",
};

// Turn a numeric (or legacy string) enum value into a readable label.
function labelFrom(map, value) {
  if (value === null || value === undefined || value === "") return "—";
  // numeric or numeric-string → look up the enum table
  const asNum = typeof value === "number" ? value : (/^\d+$/.test(String(value)) ? Number(value) : null);
  if (asNum !== null) return map[asNum] ?? `Unknown (${asNum})`;
  // legacy free-text value (e.g. "Listed", "physical") → show as-is
  return String(value);
}

export const productStatusLabel = (value) => labelFrom(PRODUCT_STATUS, value);
export const productTypeLabel = (value) => labelFrom(PRODUCT_TYPE, value);

// Tone key for coloured Status pills. Legacy "Listed" reads as active/green.
export function productStatusTone(value) {
  const asNum = typeof value === "number" ? value : (/^\d+$/.test(String(value ?? "")) ? Number(value) : null);
  if (asNum === 1) return "green";
  if (asNum === 88) return "amber";
  if (asNum === 99) return "red";
  if (typeof value === "string" && value.toLowerCase() === "listed") return "green";
  return "slate";
}

// Dropdown option lists (label-sorted for Type; fixed order for Status).
export const PRODUCT_STATUS_OPTIONS = Object.entries(PRODUCT_STATUS)
  .map(([value, label]) => ({ value: Number(value), label }));

export const PRODUCT_TYPE_OPTIONS = Object.entries(PRODUCT_TYPE)
  .map(([value, label]) => ({ value: Number(value), label }))
  .sort((a, b) => a.label.localeCompare(b.label));
